import React, { useRef, useState } from 'react';

import { useTranslations } from '#@/modules/translations';

import Input, { type InputProps, type InputRenderProps } from '../Input';
import { BorderlessInput, ClearIcon, SearchInputElement } from './Styles';

export interface SearchInputProps extends Omit<InputProps, 'value'> {
  forwardRef?: React.RefObject<HTMLInputElement>;
  initialValue?: string;
  value?: string;
  borderless?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  onClear?: () => void;
}

const SearchInput: React.FC<SearchInputProps> = ({
  forwardRef,
  initialValue,
  onChange,
  onClear,
  value: valueProp,
  borderless,
  placeholder,
  ...props
}) => {
  const localRef = useRef<HTMLInputElement>(null);
  const [localValue, setValue] = useState(initialValue ?? '');
  const value = valueProp ?? localValue;

  const translations = useTranslations('searchInput');

  const inputRef = forwardRef ?? localRef;
  const InputComponent = borderless ? BorderlessInput : Input;

  return (
    <InputComponent
      ref={inputRef}
      icon="search"
      placeholder={placeholder ?? translations.search}
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
        onChange(newValue);
      }}
      renderInput={(inputProps: InputRenderProps) => (
        <>
          <SearchInputElement
            {...inputProps}
            onClick={(e) => e.stopPropagation()}
            onChange={(event) => inputProps.onChange(event.target.value, event)}
          />
          {value !== '' && (
            <ClearIcon
              type="close"
              onClick={(e) => {
                e.stopPropagation();

                setValue('');
                onChange('');
                onClear?.();
                inputRef.current?.focus();
              }}
              title={translations.clearButtonLabel}
              size={18}
            />
          )}
        </>
      )}
      {...props}
    />
  );
};

SearchInput.displayName = 'SearchInput';

export default SearchInput;
